<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  extra: { type: Object, default: () => ({}) },
  isSubmitting: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const form = ref({
  name: '',
  description: '',
  price: 0,
  price_type: 'per_night',
  active: true,
});

const priceTypes = computed(() => [
  { value: 'per_night', label: t('MOTOR_RESERVA.EXTRAS.PRICE_TYPES.PER_NIGHT') },
  { value: 'per_stay', label: t('MOTOR_RESERVA.EXTRAS.PRICE_TYPES.PER_STAY') },
  { value: 'per_person', label: t('MOTOR_RESERVA.EXTRAS.PRICE_TYPES.PER_PERSON') },
]);

watch(
  () => props.extra,
  val => {
    if (val && val.id) {
      form.value = {
        name: val.name || '',
        description: val.description || '',
        price: val.price || 0,
        price_type: val.price_type || 'per_night',
        active: val.active !== false,
      };
    }
  },
  { immediate: true }
);

const handleSubmit = () => {
  emit('submit', { motor_reserva_extra: { ...form.value } });
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
        {{ t('MOTOR_RESERVA.EXTRAS.FORM.NAME') }}
      </label>
      <input
        v-model="form.name"
        type="text"
        required
        :placeholder="t('MOTOR_RESERVA.EXTRAS.FORM.NAME_PLACEHOLDER')"
        class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
        {{ t('MOTOR_RESERVA.EXTRAS.FORM.DESCRIPTION') }}
      </label>
      <textarea
        v-model="form.description"
        :placeholder="t('MOTOR_RESERVA.EXTRAS.FORM.DESCRIPTION_PLACEHOLDER')"
        rows="2"
        class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
      />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ t('MOTOR_RESERVA.EXTRAS.FORM.PRICE') }}
        </label>
        <input
          v-model.number="form.price"
          type="number"
          step="0.01"
          min="0"
          class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ t('MOTOR_RESERVA.EXTRAS.FORM.PRICE_TYPE') }}
        </label>
        <select
          v-model="form.price_type"
          class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
        >
          <option
            v-for="pt in priceTypes"
            :key="pt.value"
            :value="pt.value"
          >
            {{ pt.label }}
          </option>
        </select>
      </div>
    </div>

    <div class="flex items-center gap-2">
      <input
        v-model="form.active"
        type="checkbox"
        class="rounded border-slate-300"
      />
      <label class="text-sm text-slate-700 dark:text-slate-200">
        {{ t('MOTOR_RESERVA.EXTRAS.FORM.ACTIVE') }}
      </label>
    </div>

    <div class="flex gap-2">
      <Button
        type="submit"
        :label="t('MOTOR_RESERVA.EXTRAS.FORM.SUBMIT')"
        :is-loading="isSubmitting"
      />
      <Button
        type="button"
        variant="secondary"
        label="Cancelar"
        @click="emit('cancel')"
      />
    </div>
  </form>
</template>
